import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ImagePlus, PencilLine, ShoppingBag, Trash2, Upload } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  EmptyState,
  PanelActionButton,
  SectionPanel,
  StatusPill,
} from "@/components/platform/ContentPrimitives";
import LocationPickerMap from "@/components/LocationPickerMap";
import { useAuth } from "@/contexts/AuthContext";
import { MultiSelectGrid } from "@/features/auth/register-wizard/primitives";
import { useTerritoryCatalog } from "@/hooks/useTerritoryCatalog";
import { api } from "@/lib/api";
import {
  DIRECTORY_FOCUS_OPTIONS,
  DIRECTORY_TYPE_OPTIONS,
  getDirectoryTypeLabel,
} from "@/lib/polenica";

const VALUE_OPTIONS = [
  { value: "fijo", label: "Valor fijo" },
  { value: "negociable", label: "Negociable" },
  { value: "intercambio", label: "Intercambio" },
];

const EMPTY_FORM = {
  tipo_oferta: DIRECTORY_TYPE_OPTIONS[0].value,
  titulo: "",
  descripcion: "",
  tipo_valor: "fijo",
  precio: "",
  busca_intercambio: "",
  direccion: "",
  municipio: "Medellin",
  comuna: "",
  barrio: "",
  enfoques_asociados: [],
  nombre_contacto: "",
  celular_contacto: "",
  email_contacto: "",
  sitio_web: "",
  instagram_url: "",
  facebook_url: "",
  tiktok_handle: "",
  x_handle: "",
  whatsapp_url: "",
  acepta_deslinde_responsabilidad: false,
  imagenes_texto: "",
};

function inputClass() {
  return "h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/35";
}

function textareaClass() {
  return "w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 py-3 outline-none transition focus:border-primary/35";
}

function InputField({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-primary">{label}</span>
      {children}
      {hint ? <span className="mt-2 block text-xs text-mutedForeground">{hint}</span> : null}
    </label>
  );
}

function splitLines(value) {
  return String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function getTypeConfig(typeValue) {
  return DIRECTORY_TYPE_OPTIONS.find((option) => option.value === typeValue) || DIRECTORY_TYPE_OPTIONS[0];
}

function normalizeForm(post) {
  return {
    tipo_oferta: post.tipo_oferta || DIRECTORY_TYPE_OPTIONS[0].value,
    titulo: post.titulo || "",
    descripcion: post.descripcion || "",
    tipo_valor: post.tipo_valor || "fijo",
    precio: post.precio ?? "",
    busca_intercambio: post.busca_intercambio || "",
    direccion: post.direccion || "",
    municipio: post.municipio || "Medellin",
    comuna: post.comuna || "",
    barrio: post.barrio || "",
    enfoques_asociados: Array.isArray(post.enfoques_asociados) ? post.enfoques_asociados : [],
    nombre_contacto: post.nombre_contacto || "",
    celular_contacto: post.celular_contacto || "",
    email_contacto: post.email_contacto || "",
    sitio_web: post.sitio_web || "",
    instagram_url: post.instagram_url || "",
    facebook_url: post.facebook_url || "",
    tiktok_handle: post.tiktok_handle || "",
    x_handle: post.x_handle || "",
    whatsapp_url: post.whatsapp_url || "",
    acepta_deslinde_responsabilidad: Boolean(post.acepta_deslinde_responsabilidad),
    imagenes_texto: Array.isArray(post.imagenes) ? post.imagenes.join("\n") : "",
  };
}

async function uploadImages(files) {
  const uploads = await Promise.all(
    files.map(async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/uploads/images", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    }),
  );

  return {
    imageUrls: uploads.map((item) => item.secure_url),
    assetPublicIds: uploads.map((item) => item.asset_public_id).filter(Boolean),
  };
}

function updateSearchParam(setSearchParams, key, value) {
  setSearchParams((current) => {
    const next = new URLSearchParams(current);
    if (value == null || value === "") {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    return next;
  });
}

export default function CreatePost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  const territory = useTerritoryCatalog();
  const [form, setForm] = useState(EMPTY_FORM);
  const [myPosts, setMyPosts] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [coordinate, setCoordinate] = useState(null);

  const previewImages = useMemo(() => {
    const uploaded = files.map((file) => URL.createObjectURL(file));
    const typed = splitLines(form.imagenes_texto);
    return [...typed, ...uploaded].slice(0, 5);
  }, [files, form.imagenes_texto]);

  const comunaOptions = territory.getComunasForMunicipio(form.municipio);
  const barrioOptions = territory.getBarriosForComuna(form.municipio, form.comuna);

  useEffect(() => {
    return () => {
      previewImages.forEach((item) => {
        if (item.startsWith("blob:")) {
          URL.revokeObjectURL(item);
        }
      });
    };
  }, [previewImages]);

  const fetchMyPosts = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const postsResponse = await api.get("/marketplace/my-posts");
      setMyPosts(postsResponse.data || []);
    } catch (_error) {
      toast.error("No fue posible cargar su informacion del directorio.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMyPosts();
  }, [fetchMyPosts]);

  useEffect(() => {
    if (!editId) {
      setForm((current) => ({
        ...EMPTY_FORM,
        nombre_contacto: current.nombre_contacto || `${user?.nombres || ""} ${user?.apellidos || ""}`.trim(),
        celular_contacto: current.celular_contacto || user?.celular || "",
        email_contacto: current.email_contacto || user?.email || "",
      }));
      setCoordinate(null);
      return;
    }

    const target = myPosts.find((post) => String(post.id) === String(editId));
    if (target) {
      setForm(normalizeForm(target));
      if (target.latitud != null && target.longitud != null) {
        setCoordinate({
          lat: Number(target.latitud),
          lng: Number(target.longitud),
        });
      }
      return;
    }

    api
      .get(`/marketplace/${editId}`)
      .then((response) => {
        const post = response.data;
        setForm(normalizeForm(post));
        if (post.latitud != null && post.longitud != null) {
          setCoordinate({ lat: Number(post.latitud), lng: Number(post.longitud) });
        }
      })
      .catch(() => toast.error("No fue posible cargar la publicación para editarla."));
  }, [editId, myPosts, user]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleToggleFocus = (item) => {
    setForm((current) => ({
      ...current,
      enfoques_asociados: current.enfoques_asociados.includes(item)
        ? current.enfoques_asociados.filter((value) => value !== item)
        : [...current.enfoques_asociados, item],
    }));
  };

  const handleMunicipalityChange = (municipio) => {
    setForm((current) => ({
      ...current,
      municipio,
      comuna: municipio === "Medellin" ? current.comuna : "",
      barrio: municipio === "Medellin" ? current.barrio : "",
    }));
  };

  const handleComunaChange = (comuna) => {
    setForm((current) => ({
      ...current,
      comuna,
      barrio: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      let assetPublicIds = [];
      let uploadedUrls = [];
      if (files.length) {
        const uploaded = await uploadImages(files.slice(0, 5));
        assetPublicIds = uploaded.assetPublicIds;
        uploadedUrls = uploaded.imageUrls;
      }

      const typeConfig = getTypeConfig(form.tipo_oferta);
      const manualUrls = splitLines(form.imagenes_texto).slice(0, 5);
      const payload = {
        tipo: typeConfig.publicationType,
        tipo_oferta: form.tipo_oferta,
        titulo: form.titulo,
        categoria: typeConfig.category,
        descripcion: form.descripcion,
        tipo_valor: form.tipo_valor,
        precio: form.tipo_valor === "intercambio" ? null : form.precio === "" ? null : Number(form.precio),
        busca_intercambio: form.tipo_valor === "intercambio" ? form.busca_intercambio : null,
        imagenes: [...manualUrls, ...uploadedUrls].slice(0, 5),
        asset_public_ids: assetPublicIds,
        direccion: form.direccion || null,
        latitud: coordinate?.lat ?? null,
        longitud: coordinate?.lng ?? null,
        municipio: form.municipio || null,
        comuna: form.comuna || null,
        barrio: form.barrio || null,
        initiative_id: null,
        nombre_iniciativa: null,
        enfoques_asociados: form.enfoques_asociados,
        nombre_contacto: form.nombre_contacto || null,
        celular_contacto: form.celular_contacto || null,
        email_contacto: form.email_contacto || null,
        sitio_web: form.sitio_web || null,
        instagram_url: form.instagram_url || null,
        facebook_url: form.facebook_url || null,
        tiktok_handle: form.tiktok_handle || null,
        x_handle: form.x_handle || null,
        whatsapp_url: form.whatsapp_url || null,
        vigencia: "indefinido",
        acepta_deslinde_responsabilidad: form.acepta_deslinde_responsabilidad,
      };

      if (editId) {
        await api.put(`/marketplace/${editId}`, payload);
        toast.success("Publicación actualizada correctamente.");
      } else {
        await api.post("/marketplace", payload);
        toast.success("Publicación creada y publicada correctamente.");
      }

      setFiles([]);
      setCoordinate(null);
      updateSearchParam(setSearchParams, "edit", null);
      setForm({
        ...EMPTY_FORM,
        nombre_contacto: `${user?.nombres || ""} ${user?.apellidos || ""}`.trim(),
        celular_contacto: user?.celular || "",
        email_contacto: user?.email || "",
      });
      await fetchMyPosts();
      navigate("/directorio");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No fue posible guardar la publicacion.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await api.delete(`/marketplace/${postId}`);
      toast.success("Publicacion eliminada.");
      if (String(editId) === String(postId)) {
        updateSearchParam(setSearchParams, "edit", null);
        setForm(EMPTY_FORM);
        setCoordinate(null);
      }
      await fetchMyPosts();
    } catch (_error) {
      toast.error("No fue posible eliminar la publicacion.");
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="grid gap-6 xl:grid-cols-[1fr_0.92fr]">
        <SectionPanel
          title={editId ? "Editar publicacion" : "Crear publicacion"}
          eyebrow="Directorio"
          description="Ponga en circulacion productos, servicios o apoyos vinculados a la red y deje visible su enfoque territorial."
        >
          {!user ? (
            <EmptyState
              icon={ShoppingBag}
              title="Debe iniciar sesion"
              description="Solo usuarios autenticados pueden publicar en el directorio."
            />
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <InputField label="Tipo de oferta">
                  <select
                    value={form.tipo_oferta}
                    onChange={(event) => handleChange("tipo_oferta", event.target.value)}
                    className={inputClass()}
                  >
                    {DIRECTORY_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputField>

                <InputField label="Titulo">
                  <input
                    value={form.titulo}
                    onChange={(event) => handleChange("titulo", event.target.value)}
                    className={inputClass()}
                    required
                  />
                </InputField>

                <InputField label="Tipo de valor">
                  <select
                    value={form.tipo_valor}
                    onChange={(event) => handleChange("tipo_valor", event.target.value)}
                    className={inputClass()}
                  >
                    {VALUE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputField>

                {form.tipo_valor === "intercambio" ? (
                  <InputField label="Que busca a cambio">
                    <input
                      value={form.busca_intercambio}
                      onChange={(event) => handleChange("busca_intercambio", event.target.value)}
                      className={inputClass()}
                    />
                  </InputField>
                ) : (
                  <InputField label="Valor referencial">
                    <input
                      type="number"
                      value={form.precio}
                      onChange={(event) => handleChange("precio", event.target.value)}
                      className={inputClass()}
                    />
                  </InputField>
                )}

                <InputField label="Municipio">
                  <select
                    value={form.municipio}
                    onChange={(event) => handleMunicipalityChange(event.target.value)}
                    className={inputClass()}
                  >
                    {territory.municipalities.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputField>

                <InputField label="Comuna o distrito">
                  <select
                    value={form.comuna}
                    onChange={(event) => handleComunaChange(event.target.value)}
                    className={inputClass()}
                    disabled={form.municipio !== "Medellin"}
                  >
                    <option value="">{form.municipio === "Medellin" ? "Seleccione una comuna" : "No aplica"}</option>
                    {comunaOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputField>

                <InputField label="Barrio o zona">
                  <select
                    value={form.barrio}
                    onChange={(event) => handleChange("barrio", event.target.value)}
                    className={inputClass()}
                    disabled={form.municipio !== "Medellin" || !form.comuna}
                  >
                    <option value="">{form.municipio === "Medellin" ? "Seleccione un barrio" : "No aplica"}</option>
                    {barrioOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputField>

              </div>

              <InputField label="Enfoques asociados">
                <MultiSelectGrid
                  options={DIRECTORY_FOCUS_OPTIONS}
                  value={form.enfoques_asociados}
                  onToggle={handleToggleFocus}
                />
              </InputField>

              <InputField label="Descripcion">
                <textarea
                  value={form.descripcion}
                  onChange={(event) => handleChange("descripcion", event.target.value)}
                  rows={5}
                  className={textareaClass()}
                  required
                />
              </InputField>

              <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
                <InputField label="Direccion o punto de referencia">
                  <input
                    value={form.direccion}
                    onChange={(event) => handleChange("direccion", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField
                  label="Ubicacion en el mapa"
                  hint="Toque el mapa para fijar el punto aproximado de la oferta o del lugar de entrega."
                >
                  <div className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-3">
                    <LocationPickerMap
                      value={coordinate}
                      onChange={setCoordinate}
                      helpText={
                        coordinate
                          ? `Punto fijado en ${coordinate.lat.toFixed(5)}, ${coordinate.lng.toFixed(5)}.`
                          : "Toque el mapa para ubicar el punto aproximado."
                      }
                    />
                  </div>
                </InputField>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InputField label="Nombre de contacto">
                  <input
                    value={form.nombre_contacto}
                    onChange={(event) => handleChange("nombre_contacto", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField label="Celular de contacto">
                  <input
                    value={form.celular_contacto}
                    onChange={(event) => handleChange("celular_contacto", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField label="Correo de contacto">
                  <input
                    type="email"
                    value={form.email_contacto}
                    onChange={(event) => handleChange("email_contacto", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField label="Sitio web">
                  <input
                    value={form.sitio_web}
                    onChange={(event) => handleChange("sitio_web", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField label="Instagram">
                  <input
                    value={form.instagram_url}
                    onChange={(event) => handleChange("instagram_url", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField label="Facebook">
                  <input
                    value={form.facebook_url}
                    onChange={(event) => handleChange("facebook_url", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField label="TikTok">
                  <input
                    value={form.tiktok_handle}
                    onChange={(event) => handleChange("tiktok_handle", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField label="X">
                  <input
                    value={form.x_handle}
                    onChange={(event) => handleChange("x_handle", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>

                <InputField label="WhatsApp">
                  <input
                    value={form.whatsapp_url}
                    onChange={(event) => handleChange("whatsapp_url", event.target.value)}
                    className={inputClass()}
                  />
                </InputField>
              </div>

              <div className="grid gap-4 lg:grid-cols-[1fr_0.9fr]">
                <InputField label="Imagenes por URL" hint="Puede pegar hasta 5 URL, una por linea.">
                  <textarea
                    value={form.imagenes_texto}
                    onChange={(event) => handleChange("imagenes_texto", event.target.value)}
                    rows={5}
                    className={textareaClass()}
                  />
                </InputField>

                <div className="rounded-[24px] border border-dashed border-primary/18 bg-[#FAFBF7] p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-primary">Subir imagenes</p>
                      <p className="mt-1 text-sm leading-6 text-mutedForeground">
                        Puede adjuntar hasta 5 archivos desde este equipo.
                      </p>
                    </div>
                    <Upload className="h-5 w-5 text-primary" />
                  </div>
                  <label className="mt-4 flex cursor-pointer items-center justify-center rounded-2xl border border-primary/15 bg-white px-4 py-3 text-sm font-semibold text-primary">
                    <ImagePlus className="mr-2 h-4 w-4" />
                    Seleccionar archivos
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(event) => setFiles(Array.from(event.target.files || []).slice(0, 5))}
                    />
                  </label>
                </div>
              </div>

              {previewImages.length ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                  {previewImages.map((src, index) => (
                    <div key={`${src}-${index}`} className="overflow-hidden rounded-[18px] border border-primary/10 bg-[#FAFBF7]">
                      <img src={src} alt={`Vista previa ${index + 1}`} className="h-28 w-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : null}

              <label className="flex items-start gap-3 rounded-[22px] border border-primary/10 bg-[#FAFBF7] px-4 py-4">
                <input
                  type="checkbox"
                  checked={form.acepta_deslinde_responsabilidad}
                  onChange={(event) => handleChange("acepta_deslinde_responsabilidad", event.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-primary text-primary"
                />
                <span className="text-sm leading-6 text-textMain">
                  Acepto el deslinde de responsabilidad para el contenido publicado en el directorio.
                </span>
              </label>

              <div className="flex flex-wrap gap-3">
                <PanelActionButton type="submit" disabled={saving}>
                  <ShoppingBag className="h-4 w-4" />
                  {saving ? "Guardando..." : editId ? "Actualizar publicacion" : "Crear publicacion"}
                </PanelActionButton>
                {editId ? (
                  <PanelActionButton
                    tone="secondary"
                    onClick={() => {
                      updateSearchParam(setSearchParams, "edit", null);
                      setForm(EMPTY_FORM);
                      setCoordinate(null);
                    }}
                  >
                    Cancelar
                  </PanelActionButton>
                ) : null}
              </div>
            </form>
          )}
        </SectionPanel>

        <SectionPanel
          title="Mis publicaciones"
          eyebrow="Gestion propia"
          description="Revise el estado de sus ofertas, retome cualquier borrador y edite lo que necesite publicar."
        >
          {!user ? (
            <EmptyState
              icon={ShoppingBag}
              title="Sin cuenta activa"
              description="Inicie sesion para administrar sus publicaciones."
            />
          ) : loading ? (
            <div className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] px-6 py-12 text-center text-mutedForeground">
              Cargando publicaciones...
            </div>
          ) : !myPosts.length ? (
            <EmptyState
              icon={ShoppingBag}
              title="Aun no ha publicado en el directorio"
              description="Use el formulario para crear la primera oferta de su proceso."
            />
          ) : (
            <div className="space-y-4">
              {myPosts.map((post) => (
                <article key={post.id} className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap gap-2">
                        <StatusPill
                          tone={
                            post.estado === "PUBLICADO"
                              ? "positive"
                              : post.estado === "RECHAZADO"
                                ? "danger"
                                : "warning"
                          }
                        >
                          {post.estado || post.status}
                        </StatusPill>
                        <StatusPill tone="neutral">{getDirectoryTypeLabel(post.tipo_oferta)}</StatusPill>
                      </div>
                      <h3 className="mt-3 font-heading text-xl font-semibold text-primary">{post.titulo}</h3>
                      <p className="mt-2 text-sm leading-6 text-mutedForeground">{post.descripcion}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <PanelActionButton
                        tone="secondary"
                        onClick={() => updateSearchParam(setSearchParams, "edit", post.id)}
                      >
                        <PencilLine className="h-4 w-4" />
                        Editar
                      </PanelActionButton>
                      <PanelActionButton tone="danger" onClick={() => handleDelete(post.id)}>
                        <Trash2 className="h-4 w-4" />
                        Eliminar
                      </PanelActionButton>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </SectionPanel>
      </div>
    </div>
  );
}
